import * as qs from 'qs';
import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate, useSearchParams } from 'react-router';

import { Navigate } from '@sorare/routing';
import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { LabelL } from '@sorare/core/src/atoms/typography';
import { ExtraMessageWithBlurredBg } from '@sorare/core/src/components/onboarding/ExtraMessageWithBlurredBg';
import { HighlightOverlay } from '@sorare/core/src/components/onboarding/HighlightOverlay';
import {
  ANY_SPORT_HOME,
  ANY_SPORT_ONBOARDING,
} from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { glossary } from '@sorare/core/src/lib/glossary';

type Props = {
  children: ReactNode;
  queryLoading: boolean;
};

export const WithOnboardingComponents = ({ children, queryLoading }: Props) => {
  const { sport } = useSportContext();
  const { currentUser: contextCurrentUser } = useCurrentUserContext();
  const navigate = useNavigate();
  const { generateSportContextPath } = useSportContext();

  const { forcedIntoOnboarding } = contextCurrentUser || {};

  const [searchParams] = useSearchParams();
  const isTabletAndAbove = useIsTabletAndAbove();

  if (forcedIntoOnboarding) {
    if (queryLoading) {
      //To avoid showing the wrong so5Cover during the onboarding
      return null;
    }
    if (
      contextCurrentUser?.anyUserSportProfile?.commonGameOnboarded &&
      sport !== Sport.BASEBALL
    ) {
      return <>{children}</>;
    }
    return (
      <Navigate to={generateSportContextPath(ANY_SPORT_ONBOARDING)} replace />
    );
  }

  const onCloseMissionsOnboarding = () => {
    searchParams.delete('showMissionsOnboarding');
    navigate(
      `${generateSportContextPath(ANY_SPORT_HOME)}${qs.stringify(
        searchParams.toString(),
        {
          addQueryPrefix: true,
        }
      )}`,
      {
        preventScrollReset: true,
        replace: true,
      }
    );
  };

  return (
    <>
      {searchParams.get('showMissionsOnboarding') && (
        <HighlightOverlay
          highlightNames={['Missions']}
          withDelay
          onClose={onCloseMissionsOnboarding}
          extras={[
            {
              nextTo: 'Missions',
              render: props => (
                <ExtraMessageWithBlurredBg
                  align="center"
                  anchor="bottom"
                  positionProps={props}
                  fullWidth={!isTabletAndAbove}
                  buttonProps={{
                    color: 'primary',
                    children: <FormattedMessage {...glossary.letsGo} />,
                    onClick: onCloseMissionsOnboarding,
                  }}
                >
                  <LabelL>
                    <FormattedMessage defaultMessage="You can view your onboarding missions here to complete your training." />
                  </LabelL>
                </ExtraMessageWithBlurredBg>
              ),
            },
          ]}
        />
      )}
      {children}
    </>
  );
};
